import type * as protocol from "@codesandbox/pitcher-protocol";
import { useCreateNewTaskCommand } from "commands/definitions/devtools/useCreateNewTaskCommand";
import { useOpenChangelog } from "commands/definitions/devtools/useOpenChangelog";
import { useOpenDocumentation } from "commands/definitions/devtools/useOpenDocumentation";
import { useOpenProjectSetup } from "commands/definitions/devtools/useOpenProjectSetup";
import { useOpenVMSpecs } from "commands/definitions/devtools/useOpenVMSpecs";
import { useExplorerDownloadCommand } from "commands/definitions/explorer/useExplorerDownloadCommand";
import { useRegisterCommands } from "components/Layout/Panels/VSCode/useRegisterCommands";
import { usePitcher } from "features/utils/usePitcher";
import { useCapabilities } from "hooks/pitcher/useCapabilities";
import { useConfiguredTasks } from "hooks/pitcher/useConfiguredTasks";
import { usePermissions } from "hooks/pitcher/usePermissions";
import { memo } from "react";
import type * as vscodeApi from "vscode";

import { createRegistrator } from "commands/register";
import { executeVSCodeCommand } from "utils/vscode";

import { RegisterNewTaskPaletteCommands } from "./useRegisterNewTaskPaletteCommands";

export const devtoolSections = {
  preview: "Previews",
  task: "Tasks",
  terminal: "Terminals",
};

const useRegisterDevToolsCommands = (): void => {
  const pitcher = usePitcher();
  const pitcherCapabilities = useCapabilities();
  const pitcherPermissions = usePermissions();
  const configuredTasks = useConfiguredTasks();

  const tasksWithPorts = configuredTasks.filter((t) => t.ports.length > 0);

  const openPreview = async (
    openStrategy: "devtool" | "browser-tab",
    vscode: typeof vscodeApi,
  ) => {
    const items = tasksWithPorts.reduce(
      (prev, task) => {
        return [
          ...prev,

          { kind: -1 as const, label: task.name },
          ...task.ports.map((port) => ({
            kind: 0 as const,
            label: `Open Preview on :${port.port}`,
            task,
            port,
          })),

          {
            kind: 0 as const,
            label: "Open Logs",
            task,
          },
        ];
      },
      [] as Array<
        | {
            kind: -1;
            label: string;
          }
        | {
            kind: 0;
            label: string;
            task: protocol.task.TaskDTO;
            port?: protocol.port.Port;
          }
      >,
    );

    const result = await vscode.window.showQuickPick(items, {
      placeHolder: "Select a preview to open",
      matchOnDescription: true,
      matchOnDetail: true,
    });

    if (result) {
      if ("port" in result && result.port) {
        if (openStrategy === "devtool") {
          executeVSCodeCommand("codesandbox.openCsbTab", {
            type: "TASK_PORT",
            taskId: result.task.id,
            port: result.port.port,
          });
        } else {
          window.open(`https://${result.port.url}`);
        }
      }
    }
  };
  /**
   * PREVIEWS
   */
  useRegisterCommands(
    [
      {
        id: "codesandbox.devtools.open-preview",
        name: "Open Port in Devtool...",
        section: "Devtools",
        perform: async (vscode) => {
          openPreview("devtool", vscode);
        },
      },
      {
        id: "codesandbox.devtools.open-browser",
        name: "Open Port in Browser...",
        section: "Devtools",
        perform: async (vscode) => {
          openPreview("browser-tab", vscode);
        },
      },
    ],
    [tasksWithPorts],
  );

  const downloadProjectCommand = useExplorerDownloadCommand();
  useRegisterCommands([downloadProjectCommand], []);

  const toggleTaskPicker = useCreateNewTaskCommand();

  const openDocsKeyboardCommand = useOpenDocumentation();
  const openProjectSetupCommand = useOpenProjectSetup();
  const openVMSpecsCommand = useOpenVMSpecs();
  const openChangelogCommand = useOpenChangelog({ source: "keyboard" });
  const canCreateTasks =
    pitcherCapabilities.task?.runCommand && pitcherPermissions.task?.runCommand;

  useRegisterCommands(
    canCreateTasks
      ? [
          toggleTaskPicker,
          openDocsKeyboardCommand,
          openChangelogCommand,
          openProjectSetupCommand,
          openVMSpecsCommand,
        ]
      : [openDocsKeyboardCommand, openChangelogCommand],
    [canCreateTasks],
  );

  /**
   * Palette item for running a command from the search query
   */
  useRegisterCommands(
    canCreateTasks
      ? [
          {
            id: "codesandbox.tasks.runInCommandShell",
            name: "Run Task in Command Shell...",
            section: "Tasks",
            perform: async (vscode) => {
              const result = await vscode.window.showInputBox({
                placeHolder: "zsh",
                prompt: "Enter the command to run in a command shell",
              });

              if (result) {
                pitcher.clients.task.runCommand(result);
              }
            },
          },
        ]
      : [],

    [canCreateTasks],
  );
};

const _RegisterDevToolsCommands = createRegistrator(
  useRegisterDevToolsCommands,
);

export const RegisterDevToolsCommands = memo(() => {
  return (
    <>
      <RegisterNewTaskPaletteCommands />
      <_RegisterDevToolsCommands />
    </>
  );
});
